import { Box, Checkbox, Menu, TextField, Typography } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import useAPI from "../../../../../api/hooks/useAPI";
import useColors from "../../../../../api/hooks/useColors";
import { entityKeys, tagKeys } from "../../../../../api/queryKeys";
import TextButton from "../../../../../components/buttons/TextButton";
import ChipTag from "../../../../../components/ChipTag";
import LoadingIcon from "../../../../../components/LoadingIcon";
import { Toast } from "../../../../../components/Toast";
import { EnvFeatureFlagContext } from "../../../../../contexts/EnvFeaturesContext";
import SvgToIcon from "../../../../../icons/SvgToIcon";
import { icons } from "../../icons/iconExport";
import { RDS_ENTITIES_QUERY_KEY_PREFIX } from "../constants";
import {
  computeChangedIds,
  createOptimisticMutationHandlers,
} from "../entityMutationUtils";
import DeleteTagMenu from "./DeleteTagMenu";

const tagCheckboxState = {
  CHECKED: "checked",
  UNCHECKED: "unchecked",
  INDETERMINATE: "indeterminate",
};

const MAX_TAG_KEY_LENGTH = 32;
const MAX_TAG_VALUE_LENGTH = 64;

const findInitialCheckboxStates = (tags, selectedEntities) => {
  const checkboxStates = {};
  for (const tag of tags) {
    const allEntitiesHaveTag = selectedEntities.every((entity) =>
      entity?.custom_tags?.includes(tag.tag_id)
    );
    const someEntitiesHaveTag = selectedEntities.some((entity) =>
      entity?.custom_tags?.includes(tag.tag_id)
    );
    if (allEntitiesHaveTag) {
      checkboxStates[tag.tag_id] = tagCheckboxState.CHECKED;
    } else if (someEntitiesHaveTag) {
      checkboxStates[tag.tag_id] = tagCheckboxState.INDETERMINATE;
    } else {
      checkboxStates[tag.tag_id] = tagCheckboxState.UNCHECKED;
    }
  }
  return checkboxStates;
};

const TagsDropdown = ({ selectedEntities, menuAnchorEl, setMenuAnchorEl }) => {
  const colors = useColors();
  const { trackEvent } = useAPI();
  const queryClient = useQueryClient();
  const { isActiveRds } = useContext(EnvFeatureFlagContext);
  const initialCheckboxStates = useRef({});
  const [checkboxStates, setCheckboxStates] = useState({});
  const [hoveredTag, setHoveredTag] = useState(null);
  const [newTagKey, setNewTagKey] = useState("");
  const [newTagValue, setNewTagValue] = useState("");
  const [newTagError, setNewTagError] = useState("");
  const [tagKeyError, setTagKeyError] = useState(false);
  const [tagValueError, setTagValueError] = useState(false);
  const [isCreatingTag, setIsCreatingTag] = useState(false);
  const {
    createTag,
    getAllCustomTags,
    updateEntitiesTagsBatch,
    refreshEntitiesOverview,
    deleteTag,
  } = useAPI();

  const tagsQuery = useQuery({
    queryKey: tagKeys.all,
    queryFn: getAllCustomTags,
    select: (data) => data.data,
  });

  const allTags = useMemo(
    () => tagsQuery.data?.filter((tag) => !tag.is_deleted) || [],
    [tagsQuery.data]
  );

  useEffect(() => {
    if (
      tagsQuery.isSuccess &&
      Object.keys(initialCheckboxStates.current).length === 0 &&
      allTags.length > 0
    ) {
      initialCheckboxStates.current = findInitialCheckboxStates(
        allTags,
        selectedEntities
      );
      setCheckboxStates(initialCheckboxStates.current);
      trackEvent("User opened Tags Dropdown");
    }
  }, [tagsQuery.isSuccess, allTags, selectedEntities, trackEvent]);

  const indeterminateEligibleTags = Object.entries(
    initialCheckboxStates.current
  )
    .filter(
      ([_, checkboxState]) => checkboxState === tagCheckboxState.INDETERMINATE
    )
    .map(([tagID]) => tagID);

  const anyChangesInCheckboxStates = Object.entries(checkboxStates).some(
    ([tagID, state]) => state !== initialCheckboxStates.current[tagID]
  );

  const toggleCheckBox = (tag) => {
    const newCheckboxStates = { ...checkboxStates };

    switch (checkboxStates[tag.tag_id]) {
      case tagCheckboxState.CHECKED:
        newCheckboxStates[tag.tag_id] = indeterminateEligibleTags.includes(
          tag.tag_id
        )
          ? tagCheckboxState.INDETERMINATE
          : tagCheckboxState.UNCHECKED;
        break;
      case tagCheckboxState.INDETERMINATE:
        newCheckboxStates[tag.tag_id] = tagCheckboxState.UNCHECKED;
        break;
      case tagCheckboxState.UNCHECKED:
        newCheckboxStates[tag.tag_id] = tagCheckboxState.CHECKED;
        break;
      default:
        break;
    }

    setCheckboxStates(newCheckboxStates);
    trackEvent("User toggled a tag checkbox");
  };

  const onMenuClose = () => {
    setMenuAnchorEl(null);
    setNewTagKey("");
    setNewTagValue("");
    setNewTagError("");
    setTagKeyError(false);
    setTagValueError(false);
  };

  const updateTags = async ({ tagIDsToAdd, tagIDsToRemove }) => {
    const entityIDsToUpdate = [];
    for (const entity of selectedEntities) {
      if (
        tagIDsToAdd.every((tagId) => entity?.custom_tags?.includes(tagId)) &&
        tagIDsToRemove.every((tagId) => !entity?.custom_tags?.includes(tagId))
      )
        continue;
      else entityIDsToUpdate.push(entity.entity_id);
    }

    if (entityIDsToUpdate.length > 0) {
      await updateEntitiesTagsBatch(
        entityIDsToUpdate,
        tagIDsToAdd,
        tagIDsToRemove
      );
    }

    if (isActiveRds) {
      await refreshEntitiesOverview();
      queryClient.invalidateQueries({
        predicate: (query) =>
          typeof query.queryKey?.[0] === "string" &&
          query.queryKey[0].startsWith(RDS_ENTITIES_QUERY_KEY_PREFIX),
      });
      queryClient.invalidateQueries({
        queryKey: entityKeys.entitiesOverview(),
      });
      return;
    }
  };

  const optimisticHandlers = createOptimisticMutationHandlers({
    queryClient,
    selectedEntities,
    fieldName: "custom_tags",
    successMessage: "Tags updated successfully",
    errorMessage: "Error updating entity tags",
  });

  const updateTagsMutation = useMutation({
    mutationFn: updateTags,
    onMutate: ({ tagIDsToAdd, tagIDsToRemove }) =>
      optimisticHandlers.onMutate({
        idsToAdd: tagIDsToAdd,
        idsToRemove: tagIDsToRemove,
      }),
    onSuccess: () => Toast.success(optimisticHandlers.successMessage),
    onError: (error, variables, context) => {
      optimisticHandlers.onError(error, variables, context);
      Toast.error(optimisticHandlers.errorMessage);
    },
    onSettled: optimisticHandlers.onSettled,
  });

  const deleteTagMutation = useMutation({
    mutationFn: async (tagId) => {
      await deleteTag(tagId);
      await queryClient.refetchQueries({ queryKey: tagKeys.all });
    },
    onSuccess: (data, tagId) => {
      Toast.success("Tag deleted successfully");
      const newCheckboxStates = { ...checkboxStates };
      delete newCheckboxStates[tagId];
      setCheckboxStates(newCheckboxStates);
    },
    onError: () => {
      Toast.error("Error deleting tag");
    },
  });

  const validateTagKey = (key) => {
    if (!key.trim()) {
      return "";
    }
    if (key.trim().length > MAX_TAG_KEY_LENGTH) {
      return `Key cannot exceed ${MAX_TAG_KEY_LENGTH} characters`;
    }
    return "";
  };

  const validateTagValue = (value) => {
    if (!value.trim()) {
      return "";
    }
    if (value.trim().length > MAX_TAG_VALUE_LENGTH) {
      return `Value cannot exceed ${MAX_TAG_VALUE_LENGTH} characters`;
    }
    return "";
  };

  const checkDuplicateTag = (key, value) => {
    if (!key.trim() || !value.trim()) {
      return false;
    }
    return allTags.some(
      (tag) => tag.key === key.trim() && tag.value === value.trim()
    );
  };

  const handleTagKeyChange = (newKey) => {
    setNewTagKey(newKey);
    const keyError = validateTagKey(newKey);
    setTagKeyError(Boolean(keyError));

    if (keyError) {
      setNewTagError(keyError);
    } else {
      const valueError = validateTagValue(newTagValue);
      if (valueError) {
        setNewTagError(valueError);
      } else if (checkDuplicateTag(newKey, newTagValue)) {
        setNewTagError("Tag with this key and value already exists!");
        setTagKeyError(true);
        setTagValueError(true);
      } else {
        setNewTagError("");
        setTagKeyError(false);
        setTagValueError(false);
      }
    }
  };

  const handleTagValueChange = (newValue) => {
    setNewTagValue(newValue);
    const valueError = validateTagValue(newValue);
    setTagValueError(Boolean(valueError));

    if (valueError) {
      setNewTagError(valueError);
    } else {
      const keyError = validateTagKey(newTagKey);
      if (keyError) {
        setNewTagError(keyError);
      } else if (checkDuplicateTag(newTagKey, newValue)) {
        setNewTagError("Tag with this key and value already exists!");
        setTagKeyError(true);
        setTagValueError(true);
      } else {
        setNewTagError("");
        setTagKeyError(false);
        setTagValueError(false);
      }
    }
  };

  const validateAndCreateTag = async () => {
    if (isCreatingTag) {
      return;
    }

    if (!newTagKey.trim()) {
      setNewTagError("Please provide key!");
      setTagKeyError(true);
      return;
    }

    if (!newTagValue.trim()) {
      setNewTagError("Please provide value!");
      setTagValueError(true);
      return;
    }

    const keyError = validateTagKey(newTagKey);
    if (keyError) {
      setNewTagError(keyError);
      setTagKeyError(true);
      return;
    }

    const valueError = validateTagValue(newTagValue);
    if (valueError) {
      setNewTagError(valueError);
      setTagValueError(true);
      return;
    }

    const tagExists = allTags.some(
      (tag) => tag.key === newTagKey.trim() && tag.value === newTagValue.trim()
    );

    if (tagExists) {
      setNewTagError("Tag with this key and value already exists!");
      setTagKeyError(true);
      setTagValueError(true);
      return;
    }

    setIsCreatingTag(true);
    try {
      await createTag(newTagKey.trim(), newTagValue.trim());
      await queryClient.refetchQueries({ queryKey: tagKeys.all });

      if (selectedEntities && selectedEntities.length) {
        const updatedAllTags = await getAllCustomTags();
        const newlyCreatedTag = updatedAllTags.data.find(
          (tag) =>
            tag.key === newTagKey.trim() && tag.value === newTagValue.trim()
        );

        if (newlyCreatedTag) {
          const newCheckboxStates = { ...checkboxStates };
          newCheckboxStates[newlyCreatedTag.tag_id] = tagCheckboxState.CHECKED;
          setCheckboxStates(newCheckboxStates);
        }
      }

      setNewTagKey("");
      setNewTagValue("");
      setNewTagError("");
      setTagKeyError(false);
      setTagValueError(false);
      Toast.success("Tag created successfully");
    } catch (error) {
      const errorMessage =
        error?.response?.data?.detail || "Error creating tag";
      setNewTagError(errorMessage);
      setTagKeyError(true);
      setTagValueError(true);
      Toast.error(errorMessage);
    } finally {
      setIsCreatingTag(false);
    }
  };

  /** Applies tag changes by computing diff and triggering optimistic mutation. */
  const handleApplyChanges = () => {
    trackEvent("User clicked Apply changes button in Tags Dropdown");

    const { idsToAdd: tagIDsToAdd, idsToRemove: tagIDsToRemove } =
      computeChangedIds(
        checkboxStates,
        initialCheckboxStates.current,
        tagCheckboxState.CHECKED,
        tagCheckboxState.UNCHECKED
      );

    onMenuClose();
    updateTagsMutation.mutate({ tagIDsToAdd, tagIDsToRemove });
  };

  return (
    <Menu
      id="tags-menu"
      anchorEl={menuAnchorEl}
      open={Boolean(menuAnchorEl)}
      onClose={onMenuClose}
      sx={{
        ".MuiPaper-root": {
          borderRadius: "9px",
          border: "1px solid " + colors.grey_200,
          boxShadow: "0px 1px 4px 0px " + colors.grey_500,
          display: "flex",
          padding: "12px",
          flexDirection: "column",
          minWidth: "400px",
        },
        "& .MuiMenu-list": {
          padding: "0",
        },
      }}
    >
      {tagsQuery.isLoading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="80px"
        >
          <LoadingIcon size={12} />
        </Box>
      ) : tagsQuery.isSuccess ? (
        <Box display="flex" flexDirection="column" gap={1}>
          <Box display="flex" flexDirection="column" gap={1}>
            <Typography
              variant="caption"
              sx={{
                color: colors.text_secondary,
                fontWeight: 500,
              }}
            >
              Add New Tag
            </Typography>
            <Box display="flex" flexDirection="column" gap={0.5} flex={1}>
              <Box display="flex" gap={1} alignItems="flex-start">
                <TextField
                  placeholder="key"
                  variant="outlined"
                  size="small"
                  value={newTagKey}
                  onChange={(e) => {
                    e.stopPropagation();
                    handleTagKeyChange(e.target.value);
                  }}
                  onKeyDown={(e) => {
                    e.stopPropagation();
                  }}
                  error={tagKeyError}
                  sx={{
                    flex: 1,
                    "& .MuiInputBase-input": {
                      fontSize: "12px",
                      padding: "6px 8px",
                    },
                  }}
                />
                <TextField
                  placeholder="value"
                  variant="outlined"
                  size="small"
                  value={newTagValue}
                  onChange={(e) => {
                    handleTagValueChange(e.target.value);
                  }}
                  onKeyDown={(e) => {
                    e.stopPropagation();
                  }}
                  error={tagValueError}
                  sx={{
                    flex: 1,
                    "& .MuiInputBase-input": {
                      fontSize: "12px",
                      padding: "6px 8px",
                    },
                  }}
                />{" "}
                <Box display="flex" gap={1} justifyContent="flex-start">
                  <TextButton
                    onClick={validateAndCreateTag}
                    color="primary"
                    size="small"
                    disabled={
                      !newTagKey.trim() ||
                      !newTagValue.trim() ||
                      newTagError ||
                      isCreatingTag
                    }
                    loading={isCreatingTag}
                    sx={{
                      justifyContent: "flex-start",
                    }}
                  >
                    <Typography variant="caption">Create tag</Typography>
                  </TextButton>
                </Box>
              </Box>
            </Box>

            {newTagError && (
              <Typography
                variant="caption"
                sx={{
                  color: colors.error_main,
                  fontSize: "11px",
                }}
              >
                {newTagError}
              </Typography>
            )}
          </Box>

          {allTags.length > 0 && (
            <>
              <Box
                sx={{
                  borderTop: `1px solid ${colors.grey_200}`,
                  paddingTop: "8px",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: colors.text_secondary,
                    fontWeight: 500,
                  }}
                >
                  Existing Tags
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "nowrap",
                  overflowY: "auto",
                  overflowX: "hidden",
                  flexDirection: "column",
                  maxHeight: "250px",
                  "&::-webkit-scrollbar": { width: 4 },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: colors.grey_400,
                    borderRadius: 4,
                  },
                }}
              >
                {allTags.map((tag) => (
                  <Box
                    key={tag.tag_id}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    gap={0.5}
                    width="100%"
                    onMouseEnter={() => setHoveredTag(tag.tag_id)}
                    onMouseLeave={() => setHoveredTag(null)}
                    sx={{
                      padding: "4px 4px",
                      "&:hover": {
                        backgroundColor: colors.grey_4percent,
                        borderRadius: "6px",
                      },
                    }}
                  >
                    <Box display="flex" alignItems="center" flex={1} gap={1}>
                      <Checkbox
                        checked={
                          checkboxStates[tag.tag_id] ===
                          tagCheckboxState.CHECKED
                        }
                        indeterminate={
                          checkboxStates[tag.tag_id] ===
                          tagCheckboxState.INDETERMINATE
                        }
                        icon={
                          <SvgToIcon
                            IconLogo={icons.Unchecked}
                            style={{ width: "16px", height: "16px" }}
                          />
                        }
                        checkedIcon={
                          <SvgToIcon
                            IconLogo={icons.Checked}
                            style={{ width: "16px", height: "16px" }}
                          />
                        }
                        indeterminateIcon={
                          <SvgToIcon
                            IconLogo={icons.Indeterminate}
                            style={{ width: "16px", height: "16px" }}
                          />
                        }
                        onChange={() => toggleCheckBox(tag)}
                        disabled={selectedEntities?.length === 0}
                        sx={{ padding: "8px" }}
                      />
                      <ChipTag tagKey={tag.key} tagValue={tag.value} />
                    </Box>
                    {hoveredTag === tag.tag_id && (
                      <DeleteTagMenu
                        tag={tag}
                        onDeleteTag={(tagId) => deleteTagMutation.mutate(tagId)}
                        deleteTagMutation={deleteTagMutation}
                      />
                    )}
                  </Box>
                ))}
              </Box>
            </>
          )}

          <Box display="flex" flexDirection="column">
            <TextButton
              onClick={handleApplyChanges}
              color="primary"
              size="small"
              disabled={
                !anyChangesInCheckboxStates || updateTagsMutation.isPending
              }
              loading={updateTagsMutation.isPending}
              sx={{
                width: "100%",
                justifyContent: "flex-start",
                paddingX: "4px",
              }}
            >
              <Box paddingLeft={0.5}>
                <Typography variant="caption">Apply changes</Typography>
              </Box>
            </TextButton>
          </Box>
        </Box>
      ) : null}
    </Menu>
  );
};

export default TagsDropdown;
